/**
 * /superadmin/orbitprint/log — the Log shelf. Answers: "who did what, when?" across every org.
 * ?org_id= narrows it to one organization (the detail page links here).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import PlatformAdminShell, { HOUSE, Badge, RouteState, card, useSuperAdminGate } from '../PlatformAdminShell';
import { orbitAdminApi, SHELVES, ACTION_LABELS, fmtDateTime, errorKind } from './orbitAdmin';

const ACTION_COLOR = { set_plan: HOUSE.ok, extend: HOUSE.ok, suspended: HOUSE.bad, deactivated: HOUSE.bad, active: HOUSE.info, notes: HOUSE.mute, impersonate: HOUSE.amber };

export default function OrbitAdminLogPage() {
  const ready = useSuperAdminGate();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const orgId = params.get('org_id') || '';
  const api = useMemo(() => orbitAdminApi(), []);
  const [rows, setRows] = useState([]);
  const [state, setState] = useState('loading');

  const load = useCallback(() => {
    setState('loading');
    api.get('/log', { params: { org_id: orgId || undefined, limit: 300 } })
      .then(r => { setRows(r.data || []); setState('ok'); })
      .catch(e => setState(errorKind(e)));
  }, [api, orgId]);
  useEffect(() => { if (ready) load(); }, [ready, load]);

  return (
    <PlatformAdminShell product="orbitprint" title="Log" shelves={SHELVES} active="log"
      headerRight={
        <button onClick={load} data-testid="orbit-admin-log-refresh"
          style={{ background: HOUSE.card, border: `1px solid ${HOUSE.line2}`, color: HOUSE.mute, borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      }>
      {!ready ? null : (
        <div style={{ padding: 16 }}>
          {orgId && (
            <p className="text-xs mb-3" style={{ color: HOUSE.mute }} data-testid="orbit-admin-log-scope">
              Showing one organization · <button onClick={() => setParams({}, { replace: true })} style={{ background: 'none', border: 'none', color: HOUSE.accent, cursor: 'pointer', padding: 0, fontSize: 12 }}>show all</button>
            </p>
          )}
          {state === 'loading' ? <RouteState kind="loading" /> :
           state === 'denied' ? <RouteState kind="denied" message="Super admin access required." /> :
           state === 'error' ? <RouteState kind="error" message="Could not load the log." onRetry={load} /> :
           rows.length === 0 ? <RouteState kind="empty" message="No admin actions yet." /> : (
            <div style={{ ...card, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="orbit-admin-log-table">
                <thead>
                  <tr style={{ color: HOUSE.dim, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, textAlign: 'left' }}>
                    {['When', 'Organization', 'Action', 'By', 'Details'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', borderBottom: `1px solid ${HOUSE.line2}`, fontWeight: 800, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(x => (
                    <tr key={x.id} style={{ borderBottom: `1px solid ${HOUSE.line}` }}>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: HOUSE.mute }}>{fmtDateTime(x.at)}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <button onClick={() => navigate(`/superadmin/orbitprint/orgs/${x.org_id}`)} style={{ background: 'none', border: 'none', color: HOUSE.text, cursor: 'pointer', padding: 0, fontFamily: 'Oswald, sans-serif', fontWeight: 700, textTransform: 'uppercase', fontSize: 13 }}>
                          {x.org_name || x.org_id}
                        </button>
                      </td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}><Badge color={ACTION_COLOR[x.action] || HOUSE.mute}>{ACTION_LABELS[x.action] || x.action}</Badge></td>
                      <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: HOUSE.body }}>{x.by}</td>
                      <td style={{ padding: '10px 14px', color: HOUSE.mute, fontSize: 12 }}>
                        {Object.entries(x.payload || {}).filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== 0).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </PlatformAdminShell>
  );
}
